/// <reference types="google.maps" />
import globals from "@lvt/globals";
import { Box, Button, Typography } from "@material-ui/core";
import { FilterCenterFocus } from "@material-ui/icons";
import GoogleMapReact from "google-map-react";
import Lottie from "lottie-react";
import { useEffect, useRef, useState, useCallback, useMemo, memo } from "react";
import { shallowEqual } from "react-redux";
import useSupercluster from "use-supercluster";

import loaderAnimation from "@/assets/animations/loader.json";
import { ArchiveCabinetMarker } from "@/common/components/MapLandingPage/Map/ArchiveCabinetMarker";
import { ClusterMarker } from "@/common/components/MapLandingPage/Map/ClusterMarker";
import { DeactivatedUnitMarker } from "@/common/components/MapLandingPage/Map/DeactivatedUnitMarker";
import { LiveUnitMarker } from "@/common/components/MapLandingPage/Map/LiveUnitMarker";
import {
    boundsForPoints,
    hasValidLatLong,
    mapFitBounds,
    searchToMapFeatureData,
    LIVE_UNIT_LEVEL_ZOOM,
    DEFAULT_MAP_CONFIG,
} from "@/common/components/MapLandingPage/Map/MapHelper";
import { mapStyles } from "@/common/components/MapLandingPage/Map/MapStyles";
import { MapSearchBarStyles } from "@/common/components/MapLandingPage/Map/MapStyles";
import { MapFeatureData, MarkerClickParams } from "@/common/components/MapLandingPage/Map/types";
import { VmsSearch } from "@/common/components/VmsSearch";
import { RootState, useDispatch, useSelector } from "@/store";
import { mapSelectors, setSelectedUnit } from "@/store/map/slice";
import { useFullStory } from "@/utilities/hooks/fullstory";
import { PageType } from "@/utilities/types/common";
import { UnitType } from "@/utilities/types/liveunit";
import { MapConfig } from "@/utilities/types/map";
import { ArchiveCabinetSearchData, LiveUnitSearchData, ResultTypes, SearchSourceType } from "@/utilities/types/search";


const comparePropsForRerender = (prevProps: MapProps, nextProps: MapProps) =>
    prevProps.isFetching === nextProps.isFetching && shallowEqual(prevProps.units, nextProps.units) && prevProps.firstSuccess === nextProps.firstSuccess;

/*
I think the idea is to decouple this component from the store more.
Starting off with just passing the units in.
*/
interface MapProps {
    units: Array<ResultTypes>;
    isFetching: boolean;
    firstSuccess: boolean;
    initialMapConfig: MapConfig;
}

const Map: React.FC<MapProps> = memo(({ units, isFetching, firstSuccess, initialMapConfig }) => {
    const FullStory = useFullStory();
    const classes = mapStyles();
    const mapRef = useRef<google.maps.Map>();
    const mapsApiRef = useRef<typeof google.maps>();
    const dispatch = useDispatch();
    const isArchiveCabinet = useSelector(mapSelectors.getIsArchiveCabinet);
    const selectedUnit = useSelector(mapSelectors.getSelectedUnit);
    const deactivatedFilter = useSelector((state: RootState) => state.map.deactivatedFilter);
    const archiveCabinetFilter: boolean = useSelector((state: RootState) => state.map.archiveCabinetFilter);
    const liveUnitFilter = useSelector((state: RootState) => state.map.liveUnitFilter);
    const [mapConfig, setMapConfig] = useState<MapConfig>(initialMapConfig);

    const type = isArchiveCabinet ? SearchSourceType.ArchiveCabinet : SearchSourceType.LiveUnit;
    const locatedUnit = units.find((unit) =>
        selectedUnit
        && unit.id === selectedUnit.id
        && unit.type == type
        && hasValidLatLong(unit),
    ) as ArchiveCabinetSearchData | LiveUnitSearchData;

    mapRef.current?.setOptions({
        styles: [
            {
                featureType: "transit",
                stylers: [{ "visibility": "off" }],
            },
            {
                featureType: "poi",
                stylers: [{ "visibility": "off" }],
            },
        ],
    });

    const mapFeatureData = useMemo(() => searchToMapFeatureData(units), [units]);

    const mapDataForCluster = useMemo(() => {
        const mapDataForCluster: MapFeatureData[] = [];

        mapFeatureData.forEach((mapDatum) => {
            switch (mapDatum.properties.unitType) {
                case archiveCabinetFilter && UnitType.ArchiveCabinet:
                    mapDataForCluster.push(mapDatum);
                    break;
                case deactivatedFilter && UnitType.Deactivated:
                    mapDataForCluster.push(mapDatum);
                    break;
                case liveUnitFilter && UnitType.LiveUnit:
                    mapDataForCluster.push(mapDatum);
                    break;
            }
        });

        return mapDataForCluster;
    }, [mapFeatureData, archiveCabinetFilter, deactivatedFilter, liveUnitFilter]);

    const { clusters, supercluster } = useSupercluster({
        points: mapDataForCluster,
        bounds: mapConfig.bounds,
        zoom: mapConfig.zoom ?? DEFAULT_MAP_CONFIG.zoom,
        options: { radius: 75, maxZoom: 20 },
    });

    const handleMapReset = () => {
        if (mapsApiRef.current && mapRef.current && mapDataForCluster.length > 0) {
            const bounds = boundsForPoints(mapsApiRef.current, mapDataForCluster);

            mapFitBounds(mapRef.current, bounds, .1, 0, 17);
        } else {
            setMapConfig(mapConfig);
        }
    };

    const clusterClick = useCallback(
        (clusterId: number) => {
            if (mapsApiRef.current && mapRef.current && supercluster) {
                const points = supercluster.getLeaves(clusterId, Infinity);
                const bounds = boundsForPoints(mapsApiRef.current, points);

                // Ensure a cluster will expand.
                const expansionZoom = supercluster.getClusterExpansionZoom(clusterId);

                mapFitBounds(mapRef.current, bounds, 0.1, expansionZoom);
            }
        },
        [supercluster, mapsApiRef, mapRef],
    );

    const markerClick = useCallback(({ unitId, isCabinet, isDeactivated }: MarkerClickParams) => {
        FullStory.event(FullStory.names.events.mapMarkerClicked, {
            id_real: unitId,
        });

        dispatch(
            setSelectedUnit({
                id: unitId,
                isArchiveCabinet: !!isCabinet,
                isDeactivated: !!isDeactivated,
            }),
        );
    }, []);

    const mapApiLoaded = mapRef.current !== undefined;

    useEffect(() => {
        if (mapRef.current) {
            mapRef.current.addListener("maptypeid_changed", () => {
                FullStory.event(FullStory.names.events.mapTypeChanged, { type_str: mapRef.current?.getMapTypeId() });
            });
        }
    }, [mapRef.current]);

    // Reset map when initial markers load
    useEffect(() => {
        if (!isFetching && mapApiLoaded && !selectedUnit) {
            handleMapReset();
        }
    }, [mapApiLoaded, mapDataForCluster, isFetching]);

    useEffect(() => {
        if (!isFetching) {
            if (locatedUnit) {
                // Use map api otherwise it will try to render lots of clusters that aren't visible.
                mapRef.current?.setCenter({
                    lat: locatedUnit.latitude as number,
                    lng: locatedUnit.longitude as number,
                });
                mapRef.current?.setZoom(LIVE_UNIT_LEVEL_ZOOM);
            } else if (selectedUnit?.id) {
                handleMapReset();
            }
        }
    }, [selectedUnit, isFetching, mapApiLoaded]);

    const mapData = mapDataForCluster.length > 500 ? clusters : mapDataForCluster;

    const createMapOptions = () => {
        return {
            scaleControl: true,
            mapTypeControl: true,
            fullscreenControl: false,
            mapTypeControlOptions: {
                style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
                position: google.maps.ControlPosition.LEFT_TOP,
            },
            restriction: {
                // Values 180 and -180 on east and west allow unlimited
                // horizontal scrolling. Values 85 and -85 on north and south
                // allow limited vertical scrolling. Google maps shows no map
                // above 85 and below -85 latitudes.
                latLngBounds: {
                    east: 180,
                    north: 85,
                    south: -85,
                    west: -180,
                },
                strictBounds: true,
            },
        };
    };

    return (
        < Box id="google-map-wrapper" data-testid="google-map-wrapper" className={classes.mapWrapper} >
            <VmsSearch customStyle={MapSearchBarStyles()} pageType={PageType.Map} />
            {firstSuccess ?
                <>
                    <Button className={classes.mapCenterButton} onClick={handleMapReset}>
                        <FilterCenterFocus />
                    </Button>
                    <GoogleMapReact
                        // Browser-visible Maps JavaScript key. Restrict it to that API and approved LVT HTTP referrers.
                        bootstrapURLKeys={{ key: globals.googleMaps.apiKey, region: "us", language: "us" }}
                        center={mapConfig.center}
                        zoom={mapConfig.zoom}
                        options={createMapOptions}
                        yesIWantToUseGoogleMapApiInternals
                        onGoogleApiLoaded={({ map, maps }) => {
                            mapRef.current = map;
                            mapsApiRef.current = maps;
                        }}
                        onChange={({ center, zoom, bounds }) => {
                            setMapConfig({
                                zoom,
                                center,
                                bounds: [bounds.nw.lng, bounds.se.lat, bounds.se.lng, bounds.nw.lat],
                            });
                        }}
                    // the experimental flag removes a few renders of the marker container,
                    // but it doesn't look like it is supported, and it didn't look like a significant performance increase.
                    // experimental={true}
                    >
                        {mapRef.current === undefined || isFetching
                            ? []
                            : mapData.map((cluster) => {
                                const [longitude, latitude] = cluster.geometry.coordinates;
                                const { cluster: isCluster, point_count: pointCount } = cluster.properties;

                                if (isCluster && "cluster_id" in cluster.properties) {
                                    return (
                                        <ClusterMarker
                                            key={`cluster-${cluster.properties.cluster_id}`}
                                            id={cluster.properties.cluster_id}
                                            lat={latitude}
                                            lng={longitude}
                                            handleClick={clusterClick}
                                            text={pointCount ? pointCount.toString() : ""}
                                        />
                                    );
                                }

                                switch (cluster.properties.unitType) {
                                    case UnitType.ArchiveCabinet:
                                        return (
                                            <ArchiveCabinetMarker
                                                key={`archive-${cluster.properties.id}`}
                                                lat={latitude}
                                                lng={longitude}
                                                text={cluster.properties.tooltip}
                                                unitId={cluster.properties.id}
                                                onClick={markerClick}
                                            />
                                        );

                                    case UnitType.Deactivated:
                                        return (
                                            <DeactivatedUnitMarker
                                                key={`deactivated-${cluster.properties.id}`}
                                                lat={latitude}
                                                lng={longitude}
                                                text={cluster.properties.tooltip}
                                                unitId={cluster.properties.id}
                                                onClick={markerClick}
                                            />
                                        );
                                    case UnitType.LiveUnit:
                                        return (
                                            <LiveUnitMarker
                                                key={`liveUnit-${cluster.properties.id}`}
                                                lat={latitude}
                                                lng={longitude}
                                                text={cluster.properties.tooltip}
                                                unitId={cluster.properties.id}
                                                onClick={markerClick}
                                            />
                                        );
                                }
                            })}
                    </GoogleMapReact>
                </>
                :
                <Box className={classes.loaderContainer}>
                    <Lottie
                        animationData={loaderAnimation}
                        title="Loading map"
                        loop={true}
                        className={classes.loader}
                    />
                    <Typography variant="h6">Loading...</Typography>
                </Box>
            }
        </Box >
    );
    // remove this
}, comparePropsForRerender);

Map.displayName = "Map";

export default Map;
